import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { fmtErr } from "@/lib/api";
import { useProject } from "@/context/ProjectContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { fmtDate } from "@/components/badges";
import { useTranslation } from "react-i18next";

export function PendingActionsCard({ canAct, isAdmin }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { selectedId } = useProject();

  const { data: pendingActions = [] } = useQuery({
    queryKey: ["payment-actions", selectedId],
    queryFn: async () => (await api.get(`/projects/${selectedId}/payment-actions`)).data,
    enabled: !!selectedId && canAct,
  });

  const approveMut = useMutation({
    mutationFn: async (id) => api.post(`/admin/payment-actions/${id}/approve`),
    onSuccess: () => {
      toast.success(t("payments.toastApprovedAction"));
      qc.invalidateQueries({ queryKey: ["payment-actions", selectedId] });
      qc.invalidateQueries({ queryKey: ["payments", selectedId] });
      qc.invalidateQueries({ queryKey: ["dashboard", selectedId] });
    },
    onError: (e) => toast.error(fmtErr(e)),
  });
  const rejectMut = useMutation({
    mutationFn: async (id) => api.post(`/admin/payment-actions/${id}/reject`, {}),
    onSuccess: () => {
      toast.success(t("payments.toastRejectedAction"));
      qc.invalidateQueries({ queryKey: ["payment-actions", selectedId] });
    },
    onError: (e) => toast.error(fmtErr(e)),
  });

  if (!canAct || pendingActions.length === 0) return null;

  return (
    <Card data-testid="payment-actions-card">
      <CardHeader className="pb-2"><CardTitle className="text-base">{t("payments.pendingCard")}</CardTitle></CardHeader>
      <CardContent className="space-y-2">
        <p className="text-xs text-slate-500">{isAdmin ? t("payments.pendingHintAdmin") : t("payments.pendingHintUser")}</p>
        {pendingActions.map((a) => (
          <div key={a.id} className="flex flex-wrap items-center gap-2 rounded-md border px-3 py-2 text-sm" data-testid={`payment-action-row-${a.id}`}>
            <Badge variant="outline">{t(`payments.action_${a.action}`)}</Badge>
            <span className="font-medium">{a.summary}</span>
            <span className="text-xs text-slate-500">{a.requested_by_email} · {fmtDate(a.created_at)}</span>
            {a.status === "pending" && isAdmin ? (
              <span className="flex gap-1 [margin-inline-start:auto]">
                <Button size="sm" variant="outline" className="text-emerald-700" onClick={() => approveMut.mutate(a.id)} data-testid={`payment-action-approve-${a.id}`}>{t("activityPlans.approve")}</Button>
                <Button size="sm" variant="outline" className="text-rose-600" onClick={() => rejectMut.mutate(a.id)} data-testid={`payment-action-reject-${a.id}`}>{t("activityPlans.reject")}</Button>
              </span>
            ) : (
              <Badge variant="outline" className={`[margin-inline-start:auto] ${a.status === "approved" ? "bg-emerald-50 text-emerald-700" : a.status === "rejected" ? "bg-rose-50 text-rose-700" : "bg-amber-50 text-amber-700"}`}>
                {a.status === "approved" ? t("badges.approved") : a.status === "rejected" ? t("activityPlans.statusRejected") : t("badges.pending")}
              </Badge>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
